/** @jsx jsx */
import React, { useRef, useState, useCallback, useMemo, Fragment } from 'react';
import { styled as styled2, jsx, css } from '@compiled/react';
// eslint-disable-next-line jira/restricted/styled-components-migration, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import Badge from '@atlaskit/badge';
import Button from '@atlaskit/button';
import ChevronDown from '@atlaskit/icon/utility/migration/chevron-down';
import { Popup, type TriggerProps } from '@atlaskit/popup'; // ignore-for-ENGHEALTH-17759
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { styledComponentWithCondition } from '@atlassian/jira-compiled-migration/src/ui/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { ThemedButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button/ThemedButton.tsx';
import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import { getIsSoftwareThemingEnabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import messages from './messages.tsx';
import type { NonEmptyArray, SelectedItems, OverflowState, OverflowConfigItem } from './types.tsx';
import { useResizeObserver as useResizeObserverLocal } from './use-resize-observer/index.tsx';
import { calcOverflow, getShouldRecomputeOverflow, getSelectedItemsCount } from './utils.tsx';

interface Props {
	/** Items that will be rendered before the "more" dropdown when visible */
	itemsBefore: OverflowConfigItem[];
	/** Items that will be rendered after the "more" dropdown when visible */
	itemsAfter: OverflowConfigItem[];
	/** A collection of item selection status (useful for rendering a count badge on the "more" dropdown) */
	selectedItems?: SelectedItems;
}

const OverflowHandler = ({ itemsBefore, itemsAfter, selectedItems = {} }: Props) => {
	const { formatMessage } = useIntl();
	const overflowContainerRef = useRef<HTMLDivElement>(null);
	const overflowContainerUlRef = useRef<HTMLUListElement>(null);
	const moreFilterRef = useRef<HTMLElement>(null);
	const moreFilterLiRef = useRef<HTMLLIElement>(null);
	const elementsRef = useRef<{ [id: string]: HTMLElement }>({});
	const [isOpen, setIsOpen] = useState<boolean>(false);

	// There is a flickering bug switching to/from the "more" button https://jplat.atlassian.net/browse/BLU-3488
	// Additional information can also be found in https://jbusiness.atlassian.net/browse/JVRQ-426
	// But can't reproduce in Storybook + lot of pending changes, so won't fix for now.
	// I suspect this is related to this out-of-rerender memorization of data.
	// Next person actively working on this file is welcome to check. See also investigation notes in the ticket above.
	const prevOverflowContainerWidthRef = useRef<number>(0);
	const elementsWidthRef = useRef<{ [id: string]: number }>({});
	const visibleElementsRef = useRef<{ [id: string]: { width: number; height: number } }>({});
	const prevElementsWidthRef = useRef<{ [id: string]: number }>({});

	const [{ overflowableHideState, isMoreDropdownHidden }, setOverflowState] =
		useState<OverflowState>({
			overflowableHideState: {},
			isMoreDropdownHidden: true,
		});

	const { renderedOverflowableItems, staticItems, renderedItemsBefore, renderedItemsAfter } =
		useMemo(() => {
			const theOverflowable: OverflowConfigItem[] = [];
			const theStatic: OverflowConfigItem[] = [];
			const theRenderedBefore: OverflowConfigItem[] = [];
			const theRenderedAfter: OverflowConfigItem[] = [];

			const categoriseItems = ({ isRenderedBefore }: { isRenderedBefore: boolean }) => {
				(isRenderedBefore ? itemsBefore : itemsAfter).forEach((item: OverflowConfigItem) => {
					const { isStatic, isHidden = false } = item;

					// Exclude hidden overflowable items from rendering
					if (!isStatic && isHidden) {
						return;
					}

					(isStatic ? theStatic : theOverflowable).push(item);
					(isRenderedBefore ? theRenderedBefore : theRenderedAfter).push(item);
				});
			};

			categoriseItems({ isRenderedBefore: true });
			categoriseItems({ isRenderedBefore: false });

			return {
				renderedOverflowableItems: theOverflowable,
				staticItems: theStatic,
				renderedItemsBefore: theRenderedBefore,
				renderedItemsAfter: theRenderedAfter,
			};
		}, [itemsBefore, itemsAfter]);

	/**
	 * Calculate the count for "More" dropdown.
	 * It excludes the selected items of the visible items.
	 */
	const selectedOverflowItemsCount = useMemo(
		() => getSelectedItemsCount(overflowableHideState, selectedItems),
		[overflowableHideState, selectedItems],
	);

	const onResize = useCallback(() => {
		const overflowContainer = fg('jfp_a11y_team_fix_filters_missing_list_mark_up')
			? overflowContainerUlRef?.current
			: overflowContainerRef?.current;
		const moreFilter = fg('jfp_a11y_team_fix_filters_missing_list_mark_up')
			? moreFilterLiRef?.current
			: moreFilterRef?.current;

		const shouldRecomputeOverflow = getShouldRecomputeOverflow(
			prevOverflowContainerWidthRef.current,
			overflowContainer?.offsetWidth ?? 0,
			prevElementsWidthRef.current,
			elementsWidthRef.current,
		);

		if (
			!overflowContainer ||
			!moreFilter ||
			!renderedOverflowableItems.length ||
			!shouldRecomputeOverflow
		) {
			return;
		}

		// Store the widths for comparison
		prevOverflowContainerWidthRef.current = overflowContainer.offsetWidth;
		prevElementsWidthRef.current = { ...elementsWidthRef.current };

		setOverflowState(
			calcOverflow(
				overflowContainer,
				moreFilter,
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				renderedOverflowableItems as NonEmptyArray<OverflowConfigItem>,
				staticItems,
				elementsRef.current,
			),
		);
	}, [renderedOverflowableItems, staticItems]);

	useResizeObserverLocal(
		fg('jfp_a11y_team_fix_filters_missing_list_mark_up')
			? overflowContainerUlRef
			: overflowContainerRef,
		onResize,
	);

	const onClick = useCallback(() => {
		setIsOpen(!isOpen);
	}, [isOpen, setIsOpen]);

	const onClose = useCallback(() => {
		setIsOpen(false);
	}, [setIsOpen]);

	const triggerLabel = formatMessage(messages.more);
	const triggerTooltipText = formatMessage(messages.moreFiltersTooltip);
	const buttonToolTip = useMemo(
		() => (
			<Tooltip content={triggerTooltipText} hideTooltipOnClick tag="span">
				<TriggerContainer data-testid="filters.common.ui.overflow-handler.trigger-container">
					<TriggerLabel>{triggerLabel}</TriggerLabel>
					{selectedOverflowItemsCount > 0 && (
						<BadgeContainer>
							<Badge appearance="primary">{selectedOverflowItemsCount}</Badge>
						</BadgeContainer>
					)}
				</TriggerContainer>
			</Tooltip>
		),
		[selectedOverflowItemsCount, triggerTooltipText, triggerLabel],
	);

	const content = useCallback(
		() => (
			<PopupContainer>
				{renderedOverflowableItems.map(
					({ id, renderer }: OverflowConfigItem) =>
						Boolean(overflowableHideState[id]) && <Fragment key={id}>{renderer(true)}</Fragment>,
				)}
			</PopupContainer>
		),
		[renderedOverflowableItems, overflowableHideState],
	);

	const renderItems = (items: OverflowConfigItem[]) =>
		items.map(({ id, isStatic, isHidden = false, renderer }: OverflowConfigItem) => {
			const isItemHidden = isStatic ? isHidden : Boolean(overflowableHideState[id]);

			if (fg('jfp_a11y_team_fix_filters_missing_list_mark_up')) {
				const isLiElementHidden = elementsRef?.current?.[id]?.offsetWidth === 0;

				return (
					<li
						key={id}
						ref={(element: HTMLLIElement) => {
							elementsRef.current[id] = element;
							if (element) {
								elementsWidthRef.current[id] = element.offsetWidth;
								if (!isHidden) {
									visibleElementsRef.current[id] = {
										width: element.offsetWidth,
										height: element.offsetHeight,
									};
								}
							}
						}}
						// Using aria-hidden and css to hide elements with an offsetWidth of 0
						// This ensures elements that do not occupy space are not announced/counted by screen readers
						aria-hidden={isItemHidden || isLiElementHidden}
						css={[isItemHidden || isLiElementHidden ? liItemHiddenStyles : liItemVisibleStyles]}
					>
						{renderer(false)}
					</li>
				);
			}

			return (
				<ItemWrapper
					key={id}
					innerRef={(element: HTMLElement) => {
						elementsRef.current[id] = element;
						if (element) {
							elementsWidthRef.current[id] = element.offsetWidth;
							if (!isHidden) {
								visibleElementsRef.current[id] = {
									width: element.offsetWidth,
									height: element.offsetHeight,
								};
							}
						}
					}}
					aria-hidden={isItemHidden}
					isHidden={isItemHidden}
				>
					{renderer(false)}
				</ItemWrapper>
			);
		});
	const { isSoftwareFullTheming: isThemed } = useSoftwareProjectTheming();
	const ButtonComponent = isThemed ? ThemedButton : Button;

	if (fg('jfp_a11y_team_fix_filters_missing_list_mark_up')) {
		return (
			<ul
				css={[
					isVisualRefreshEnabled() ? overflowContainerStyles : overflowContainerStylesOld,
					UlBoxStyles,
				]}
				ref={overflowContainerUlRef}
			>
				{renderItems(renderedItemsBefore)}
				<li
					ref={moreFilterLiRef}
					aria-hidden={isMoreDropdownHidden}
					css={[
						isMoreDropdownHidden ? liItemHiddenStyles : liItemVisibleStyles,
						isVisualRefreshEnabled() && liItemWithRightMarginStyles,
					]}
				>
					<Popup
						trigger={(triggerProps: TriggerProps) => (
							<ButtonComponent
								{...triggerProps}
								appearance={isVisualRefreshEnabled() ? 'default' : 'subtle'}
								isSelected={isOpen}
								iconAfter={
									<ChevronDown
										label=""
										{...(!(isThemed && getIsSoftwareThemingEnabled()) && {
											color: token('color.icon'),
										})}
									/>
								}
								onClick={onClick}
							>
								{buttonToolTip}
							</ButtonComponent>
						)}
						shouldRenderToParent
						shouldFlip
						isOpen={isOpen}
						placement="bottom-start"
						content={content}
						onClose={onClose}
					/>
				</li>
				{renderItems(renderedItemsAfter)}
			</ul>
		);
	}
	return (
		<div
			css={[isVisualRefreshEnabled() ? overflowContainerStyles : overflowContainerStylesOld]}
			ref={overflowContainerRef}
		>
			{renderItems(renderedItemsBefore)}
			<ItemWrapper
				innerRef={moreFilterRef}
				aria-hidden={isMoreDropdownHidden}
				isHidden={isMoreDropdownHidden}
				shouldAddRightMargin={isVisualRefreshEnabled()}
			>
				<Popup
					trigger={(triggerProps: TriggerProps) => (
						<ButtonComponent
							{...triggerProps}
							appearance={isVisualRefreshEnabled() ? 'default' : 'subtle'}
							isSelected={isOpen}
							iconAfter={
								<ChevronDown
									label=""
									{...(!(isThemed && getIsSoftwareThemingEnabled()) && {
										color: token('color.icon'),
									})}
								/>
							}
							onClick={onClick}
						>
							{buttonToolTip}
						</ButtonComponent>
					)}
					shouldRenderToParent
					shouldFlip
					isOpen={isOpen}
					placement="bottom-start"
					content={content}
					onClose={onClose}
				/>
			</ItemWrapper>
			{renderItems(renderedItemsAfter)}
		</div>
	);
};

export default OverflowHandler;

const overflowContainerStylesOld = css({
	display: 'flex',
	minWidth: 0,
});

const overflowContainerStyles = css({
	display: 'flex',
	minWidth: 0,
	marginTop: 0,
	marginRight: token('space.negative.050'),
	marginBottom: 0,
	marginLeft: token('space.negative.050'),
});

const UlBoxStyles = css({
	listStyle: 'none',
	padding: 0,
	marginTop: 0,
});

const liItemHiddenStyles = css({
	flexShrink: 0,
	order: 2,
	visibility: 'hidden',
	pointerEvents: 'none',
	marginTop: 0,
});

const liItemVisibleStyles = css({
	flexShrink: 0,
	order: 1,
	visibility: 'visible',
	pointerEvents: 'auto',
	marginTop: 0,
});

const liItemWithRightMarginStyles = css({
	marginRight: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PopupContainer = styled2.div({
	paddingTop: token('space.050'),
	paddingRight: 0,
	paddingBottom: token('space.050'),
	paddingLeft: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TriggerContainer = styled2.span({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TriggerLabel = styled2.span({
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BadgeContainer = styled2.span({
	display: 'inline-block',
	paddingLeft: token('space.050'),
});

interface ItemWrapperProps {
	isHidden: boolean;
	shouldAddRightMargin?: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const ItemWrapperControl = styled.div<ItemWrapperProps>((props) => ({
	flexShrink: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	order: props.isHidden ? 2 : 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	visibility: props.isHidden ? 'hidden' : 'visible',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	pointerEvents: props.isHidden ? 'none' : 'auto',
}));

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemWrapperExperiment = styled2.div<ItemWrapperProps>({
	flexShrink: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	order: ({ isHidden }: ItemWrapperProps) => (isHidden ? 2 : 1),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	visibility: ({ isHidden }: ItemWrapperProps) => (isHidden ? 'hidden' : 'visible'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	pointerEvents: ({ isHidden }: ItemWrapperProps) => (isHidden ? 'none' : 'auto'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginRight: ({ shouldAddRightMargin }: ItemWrapperProps) =>
		shouldAddRightMargin && token('space.050'),
});

const ItemWrapper = styledComponentWithCondition(
	() => fg('jsw_compiled_migration_tanuki'),
	ItemWrapperExperiment,
	ItemWrapperControl,
);
